/**
 * ============================================================================
 * useCourtMutations - TanStack Query mutations for court operations
 * ============================================================================
 *
 * Provides add, update, delete, and toggle mutations with automatic cache invalidation
 *
 * ============================================================================
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorApi, DeactivationBlockedError } from '../services/api';
import type { CourtFormData } from '../types/courts';
import { useCacheInvalidation } from './useCacheInvalidation';

interface CourtMutationResult {
  success: boolean;
  message: string;
  court?: any;
}

// Re-export for convenience
export { DeactivationBlockedError };

/**
 * Hook providing court mutations (add, update, delete)
 */
export function useCourtMutations(venueId: number | undefined) {
  const queryClient = useQueryClient();
  const { invalidateCourtRelated } = useCacheInvalidation();

  const addCourt = useMutation<CourtMutationResult, Error, any>({
    mutationFn: async (formData: any) => {
      if (!venueId) throw new Error('No venue ID');

      // formData now includes name and court_number from VenueCourtsNew
      const courtData = {
        name: formData.name || formData.courtName, // Support both formats
        sport_id: Number(formData.selectedSport),
        court_number: formData.court_number || 1,
        is_active: formData.isActive,
        custom_hourly_rate: formData.customHourlyRate ? Number(formData.customHourlyRate) : undefined,
        custom_per_person_rate: formData.customPerPersonRate ? Number(formData.customPerPersonRate) : undefined,
        min_players: formData.minPlayers ? Number(formData.minPlayers) : undefined,
        max_players: formData.maxPlayers ? Number(formData.maxPlayers) : undefined,
        pricing_mode: formData.pricingMode || undefined,
        allow_shared_booking: formData.allowSharedBooking || false,
        per_person_duration_mode: formData.perPersonDurationMode || 'timed',
        min_booking_duration_hours: formData.minBookingDurationHours ? Number(formData.minBookingDurationHours) : null,
        booking_slot_increment_hours: formData.bookingSlotIncrementHours ? Number(formData.bookingSlotIncrementHours) : null,
        allow_recurring_bookings: formData.allowRecurringBookings,
        recurring_discount_1_month: formData.discount1Month,
        recurring_discount_3_months: formData.discount3Months,
        recurring_discount_6_months: formData.discount6Months,
        recurring_discount_12_months: formData.discount12Months,
        // Court Sharing fields
        parent_court_id: formData.parentCourtId || null,
        share_type: formData.shareType || 'standalone',
        is_virtual: formData.isVirtual || false,
        max_concurrent_children: formData.maxConcurrentChildren ? Number(formData.maxConcurrentChildren) : null,
        physical_court_id: formData.physicalCourtId ? formData.physicalCourtId.trim() : null,
        // Slab pricing (null clears slabs on the court)
        pricing_slabs: formData.pricingSlabs?.length ? formData.pricingSlabs : null,
      };

      const result = await vendorApi.addCourt(venueId, courtData);

      return {
        success: true,
        message: 'Court added successfully!',
        court: result,
      };
    },
    onSuccess: async () => {
      // Invalidate all court-related caches across tabs
      if (venueId) {
        await invalidateCourtRelated(venueId);
      }
    },
  });

  const updateCourt = useMutation<
    CourtMutationResult,
    Error,
    { courtId: number; formData: any }
  >({
    mutationFn: async ({ courtId, formData }) => {
      if (!venueId) throw new Error('No venue ID');

      const courtData: any = {};

      // Handle court name update
      if (formData.courtName !== undefined) {
        courtData.name = formData.courtName.trim();
      }
      if (formData.isActive !== undefined) courtData.is_active = formData.isActive;
      if (formData.customHourlyRate !== undefined) {
        courtData.custom_hourly_rate = formData.customHourlyRate !== '' ? Number(formData.customHourlyRate) : null;
      }
      if (formData.customPerPersonRate !== undefined) {
        courtData.custom_per_person_rate = formData.customPerPersonRate !== '' ? Number(formData.customPerPersonRate) : null;
      }
      if (formData.minPlayers !== undefined) {
        courtData.min_players = formData.minPlayers !== '' ? Number(formData.minPlayers) : null;
      }
      if (formData.maxPlayers !== undefined) {
        courtData.max_players = formData.maxPlayers !== '' ? Number(formData.maxPlayers) : null;
      }
      if (formData.pricingMode !== undefined) {
        courtData.pricing_mode = formData.pricingMode || null;
      }
      if (formData.allowSharedBooking !== undefined) {
        courtData.allow_shared_booking = formData.allowSharedBooking;
      }
      if (formData.perPersonDurationMode !== undefined) {
        courtData.per_person_duration_mode = formData.perPersonDurationMode;
      }
      if (formData.minBookingDurationHours !== undefined) {
        courtData.min_booking_duration_hours = formData.minBookingDurationHours !== '' ? Number(formData.minBookingDurationHours) : null;
      }
      if (formData.bookingSlotIncrementHours !== undefined) {
        courtData.booking_slot_increment_hours = formData.bookingSlotIncrementHours !== '' ? Number(formData.bookingSlotIncrementHours) : null;
      }
      if (formData.allowRecurringBookings !== undefined) {
        courtData.allow_recurring_bookings = formData.allowRecurringBookings;
      }
      if (formData.discount1Month !== undefined) {
        courtData.recurring_discount_1_month = formData.discount1Month !== '' ? Number(formData.discount1Month) : 0;
      }
      if (formData.discount3Months !== undefined) {
        courtData.recurring_discount_3_months = formData.discount3Months !== '' ? Number(formData.discount3Months) : 0;
      }
      if (formData.discount6Months !== undefined) {
        courtData.recurring_discount_6_months = formData.discount6Months !== '' ? Number(formData.discount6Months) : 0;
      }
      if (formData.discount12Months !== undefined) {
        courtData.recurring_discount_12_months = formData.discount12Months !== '' ? Number(formData.discount12Months) : 0;
      }
      // Court Sharing fields
      if (formData.parentCourtId !== undefined) {
        courtData.parent_court_id = formData.parentCourtId;
      }
      if (formData.shareType !== undefined) {
        courtData.share_type = formData.shareType;
      }
      if (formData.isVirtual !== undefined) {
        courtData.is_virtual = formData.isVirtual;
      }
      if (formData.maxConcurrentChildren !== undefined) {
        courtData.max_concurrent_children = formData.maxConcurrentChildren ? Number(formData.maxConcurrentChildren) : null;
      }
      if (formData.physicalCourtId !== undefined) {
        courtData.physical_court_id = formData.physicalCourtId ? formData.physicalCourtId.trim() : null;
      }
      if (formData.pricingSlabs !== undefined) {
        courtData.pricing_slabs = formData.pricingSlabs?.length ? formData.pricingSlabs : null;
      }

      await vendorApi.updateCourt(venueId, courtId, courtData);

      return {
        success: true,
        message: 'Court updated successfully!',
      };
    },
    onSuccess: async () => {
      // Invalidate all court-related caches across tabs
      if (venueId) {
        await invalidateCourtRelated(venueId);
      }
    },
  });

  const deleteCourt = useMutation<CourtMutationResult, Error, number>({
    mutationFn: async (courtId: number) => {
      if (!venueId) throw new Error('No venue ID');

      await vendorApi.deleteCourt(venueId, courtId);

      return {
        success: true,
        message: 'Court deleted successfully!',
      };
    },
    onSuccess: async () => {
      // Invalidate all court-related caches across tabs
      if (venueId) {
        await invalidateCourtRelated(venueId);
      }
    },
  });

  /**
   * Toggle court active status
   * This is a dedicated mutation for the toggle switch - throws DeactivationBlockedError
   * when deactivation is blocked due to existing bookings
   */
  const toggleActive = useMutation<
    CourtMutationResult,
    Error | DeactivationBlockedError,
    { courtId: number; isActive: boolean }
  >({
    mutationFn: async ({ courtId, isActive }) => {
      if (!venueId) throw new Error('No venue ID');

      // This will throw DeactivationBlockedError if deactivation is blocked
      await vendorApi.updateCourt(venueId, courtId, { is_active: isActive });

      return {
        success: true,
        message: isActive ? 'Court activated successfully!' : 'Court deactivated successfully!',
      };
    },
    onSuccess: async () => {
      // Invalidate all court-related caches across tabs
      if (venueId) {
        await invalidateCourtRelated(venueId);
      }
    },
  });

  return {
    addCourt,
    updateCourt,
    deleteCourt,
    toggleActive,
  };
}
